import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	type ChangeEvent,
	type KeyboardEvent,
	useRef,
	useLayoutEffect,
} from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import EditorSearchIcon from '@atlaskit/icon/core/migration/search--editor-search';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import { Text } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { fg } from '@atlassian/jira-feature-gating';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import { ADD_GADGET_EXPERIENCE } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { useOnAddedGadget } from '@atlassian/jira-dashboard-internal-common/src/controllers/dashboard/context.tsx';
import { handleFetchErrorAsExperience } from '@atlassian/jira-dashboard-internal-common/src/utils/handle-fetch-error-as-experience/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useGadgetDirectoryResource,
	useDashboardResource,
} from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import { PACKAGE_NAME } from '../../../../constants.tsx';
import {
	useRightPanelToggle,
	useRightPanelState,
} from '../../../../controllers/right-panel-layout-controller.tsx';
import { useAddGadget } from '../../../../services/add-gadget/index.tsx';
import { AddGadgetPanelSkeleton } from './add-gadget-panel-skeleton/index.tsx';
import { Fallback, FALLBACK_TYPE } from './fallback/index.tsx';
import { GadgetDirectoryItem } from './gadget-directory-item/index.tsx';
import messages from './messages.tsx';
import {
	PanelContainer,
	PanelContentContainer,
	PanelHeaderContainer,
	Header,
	CategoryLozengeGroup,
	CategoryLozengeClickable,
} from './styled.tsx';
import { matchSearchText, sortGadget, getCategoriesFromGadgetsData } from './utils/index.tsx';

const ADD_GADGET_PANEL_HEADING_ID = 'add-gadget-panel-heading';

export const expAttributes = {
	analyticsSource: 'dashboard',
	experience: ADD_GADGET_EXPERIENCE,
	application: null,
	edition: null,
	additionalAttributes: {},
} as const;

export default function AddGadgetPanel() {
	const { formatMessage } = useIntl();
	const allCategoryLabel = useMemo(() => formatMessage(messages.all), [formatMessage]);
	const { onCloseRightPanel, onFocusPanel } = useRightPanelToggle();
	const { focusKey } = useRightPanelState();
	const inputRef = useRef<HTMLInputElement>(null);

	const { data } = useDashboardResource();
	const [activeTag, setActiveTag] = useState(allCategoryLabel);
	const [searchText, setSearchText] = useState('');
	const onBeforeGadgetAdded = useOnAddedGadget();
	const {
		addGadget,
		getIsLoading: getIsAddGadgetLoading,
		getIsDisabled: getIsAddGadgetDisabled,
	} = useAddGadget({ onBeforeGadgetAdded });

	const { data: gadgets, loading, error } = useGadgetDirectoryResource();

	const [numOfGadgetForScreenReader, setNumOfGadgetForScreenReader] = useState(
		(gadgets && gadgets.length) || 0,
	);
	// delay this a bit so that screen reader can read the number of gadgets after search event
	const debouncedUpdateNumOfGadget = useRef(
		debounce((numOfGadget) => setNumOfGadgetForScreenReader(numOfGadget), 1000, {
			trailing: true,
		}),
	);
	const onSearchTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const searchString = event.target.value.toLowerCase();
		setSearchText(searchString);
	}, []);

	const onToggleTag = useCallback(
		(tag: string) => {
			if (activeTag !== tag) {
				setActiveTag(tag);
			} else {
				setActiveTag(allCategoryLabel);
			}
		},
		[activeTag, allCategoryLabel],
	);

	const categories = useMemo(() => {
		if (gadgets != null) {
			return getCategoriesFromGadgetsData(gadgets, allCategoryLabel);
		}
		return null;
	}, [gadgets, allCategoryLabel]);

	const displayData = useMemo(() => {
		if (categories == null) {
			return null;
		}
		const filteredCategories = { ...categories };

		if (searchText.length > 0) {
			Object.keys(categories).forEach((category) => {
				filteredCategories[category] = categories[category].filter((item) =>
					matchSearchText(item, searchText),
				);
			});
		}

		const tags = Object.keys(filteredCategories).map((category) => ({
			label: category,
			count: filteredCategories[category].length,
		}));

		const sortedGadgets = sortGadget(filteredCategories[activeTag]);
		debouncedUpdateNumOfGadget.current(sortedGadgets.length);
		return { sortedGadgets, tags };
	}, [activeTag, debouncedUpdateNumOfGadget, searchText, categories]);

	useEffect(() => {
		if (error instanceof Error) {
			handleFetchErrorAsExperience({
				error,
				sendSuccessExperience: () => {
					sendExperienceAnalytics({
						...expAttributes,
						wasExperienceSuccesful: true,
					});
				},
				sendFailedExperience: () => {
					sendExperienceAnalytics({
						...expAttributes,
						wasExperienceSuccesful: false,
					});
				},
				logErrorEvent: (reason) => {
					fireErrorAnalytics({
						error,
						meta: {
							id: ADD_GADGET_EXPERIENCE,
							packageName: PACKAGE_NAME,
						},
						attributes: {
							reason,
							endpoint: 'gadgetDirectory',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}
	}, [error]);

	useLayoutEffect(() => {
		onFocusPanel(() => inputRef?.current?.focus?.());
	}, [focusKey, onFocusPanel]);

	const onKeyDownHandler = useCallback(
		({ key }: KeyboardEvent<HTMLDivElement>) => {
			if (key === 'Escape') {
				onCloseRightPanel();
			}
		},
		[onCloseRightPanel],
	);

	if (
		loading === true ||
		data == null ||
		(!error && (gadgets == null || gadgets.length === 0)) ||
		displayData == null
	) {
		return (
			<PanelContainer>
				<AddGadgetPanelSkeleton />
			</PanelContainer>
		);
	}

	return (
		<JSErrorBoundary
			id="add-gadget"
			packageName={PACKAGE_NAME}
			fallback={() => (
				<PanelContainer>
					<Fallback type={FALLBACK_TYPE.ERROR} />
				</PanelContainer>
			)}
			attributes={{ task: ADD_GADGET_EXPERIENCE, reason: 'AddGadgetPanel ErrorBoundary' }}
			withExperienceTracker
		>
			<PanelContainer
				data-testid="dashboard-edit.ui.header-editable.add-gadget-button.add-gadget-panel.panel-container"
				aria-labelledby={ADD_GADGET_PANEL_HEADING_ID}
			>
				<PanelDirectoryContainer {...{ onKeyDown: onKeyDownHandler }}>
					<PanelHeaderContainer>
						<Header>
							{fg('jfp_a11y_autodev_fix_gadget_heading') ? (
								<Heading size="xsmall" id={ADD_GADGET_PANEL_HEADING_ID} as="h2">
									{formatMessage(messages.addAGadget)}
								</Heading>
							) : (
								<Heading size="xsmall" id={ADD_GADGET_PANEL_HEADING_ID}>
									{formatMessage(messages.addAGadget)}
								</Heading>
							)}
							<IconButton
								onClick={onCloseRightPanel}
								appearance="subtle"
								label={formatMessage(messages.closeAddGadgetPanel)}
								icon={CrossIcon}
							/>
						</Header>
						<Textfield
							ref={inputRef}
							name="basic"
							aria-label={formatMessage(messages.searchGadgets)}
							placeholder={formatMessage(messages.searchGadgets)}
							onChange={onSearchTextChange}
							testId="dashboard-edit.ui.header-editable.add-gadget-button.add-gadget-panel.dashboard-add-gadget-search"
							elemBeforeInput={
								<EditorSearchIcon
									color={token('color.icon.accent.gray')}
									label=""
									spacing="spacious"
								/>
							}
						/>
						<VisuallyHidden aria-live="polite" aria-relevant="text" role="status">
							{formatMessage(messages.searchGadgetsResult, {
								num: numOfGadgetForScreenReader,
							})}
						</VisuallyHidden>
						<CategoryLozengeGroup>
							{displayData.tags.map((tag) => (
								<CategoryLozengeClickable
									isActive={activeTag === tag.label ? 'isActive' : ''}
									aria-pressed={activeTag === tag.label}
									key={tag.label}
									onClick={() => onToggleTag(tag.label)}
								>
									{tag.label}
									<Text
										weight="bold"
										color={activeTag === tag.label ? 'color.text.accent.blue' : 'color.text.subtle'}
									>
										{tag.count}
									</Text>
								</CategoryLozengeClickable>
							))}
						</CategoryLozengeGroup>
					</PanelHeaderContainer>
					<PanelContentContainer>
						{!error &&
							gadgets != null &&
							gadgets.length > 0 &&
							displayData.sortedGadgets.length === 0 && <Fallback type={FALLBACK_TYPE.NOTFOUND} />}
						{displayData.sortedGadgets.map((g) => (
							<GadgetDirectoryItem
								gadgetDirectoryItem={g}
								key={g.id}
								onAddGadget={() => addGadget(g)}
								getIsLoading={getIsAddGadgetLoading}
								getIsDisabled={getIsAddGadgetDisabled}
							/>
						))}
						{error && <Fallback type={FALLBACK_TYPE.ERROR} />}
					</PanelContentContainer>
				</PanelDirectoryContainer>
			</PanelContainer>
		</JSErrorBoundary>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelDirectoryContainer = styled.div({
	boxSizing: 'border-box',
	overflowX: 'hidden',
	overflowY: 'auto',
	height: '100%',
	width: '100%',
	right: 0,
});
